import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Building2, MapPin, Ruler, IndianRupee, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import resolveMediaUrl from '@/utils/resolveMediaUrl';

const CATEGORIES = [
  { label: 'All', value: 'all' },
  { label: 'Retail', value: 'Retail' },
  { label: 'Office', value: 'Office' },
  { label: 'SCO', value: 'SCO' },
  { label: 'Mixed Use', value: 'Mixed Use' },
  { label: 'IT Park', value: 'IT Park' },
];

const ProjectSlider = ({ projects, onEnquire }) => {
  const trackRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.project_type === activeCategory);

  useEffect(() => {
    const updateLayout = () => {
      const w = window.innerWidth;
      if (w >= 1024) setVisibleCount(3);
      else if (w >= 768) setVisibleCount(2);
      else setVisibleCount(1);

      if (trackRef.current) {
        const gap = w >= 768 ? 24 : 16;
        const containerWidth = trackRef.current.parentElement?.clientWidth || w;
        const count = w >= 1024 ? 3 : w >= 768 ? 2 : 1;
        setCardWidth((containerWidth - gap * (count - 1)) / count);
      }
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const maxIndex = Math.max(0, filteredProjects.length - visibleCount);

  const prev = () => setCurrentIndex(i => Math.max(0, i - 1));
  const next = () => setCurrentIndex(i => Math.min(maxIndex, i + 1));

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentIndex(0);
  };

  const gap = typeof window !== 'undefined' && window.innerWidth >= 768 ? 24 : 16;
  const offset = currentIndex * (cardWidth + gap);

  if (!projects.length) return null;

  return (
    <div className="relative" data-testid="project-slider">
      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="category-tabs">
        {CATEGORIES.map((cat) => {
          const count = cat.value === 'all' ? projects.length : projects.filter(p => p.project_type === cat.value).length;
          if (count === 0 && cat.value !== 'all') return null;
          const isActive = activeCategory === cat.value;
          return (
            <button
              key={cat.value}
              onClick={() => handleCategoryChange(cat.value)}
              className="px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-all duration-300"
              style={{
                fontFamily: "'Josefin Sans', sans-serif",
                background: isActive ? 'linear-gradient(135deg, #8a6a10, #c9a84c)' : 'transparent',
                color: isActive ? '#fff' : '#8a6a10',
                border: isActive ? 'none' : '1px solid rgba(201,168,76,0.3)',
              }}
              data-testid={`tab-${cat.value}`}
            >
              {cat.label} <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Arrow buttons */}
      {currentIndex > 0 && (
        <button
          onClick={prev}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #8a6a10, #c9a84c)', boxShadow: '0 4px 15px rgba(138,106,16,0.3)' }}
          data-testid="slider-prev"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      )}
      {currentIndex < maxIndex && (
        <button
          onClick={next}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #8a6a10, #c9a84c)', boxShadow: '0 4px 15px rgba(138,106,16,0.3)' }}
          data-testid="slider-next"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Slider track */}
      <div className="overflow-hidden">
        <div
          ref={trackRef}
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${offset}px)`, gap }}
        >
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="flex-shrink-0 rounded-xl overflow-hidden transition-all duration-300 group"
              style={{ width: cardWidth || '100%', border: '1px solid rgba(201,168,76,0.2)', background: '#fff' }}
              data-testid={`project-card-${project.id}`}
              data-tilt
            >
              {/* Thumbnail */}
              <div className="relative h-48 overflow-hidden">
                {project.thumbnail ? (
                  <img
                    src={resolveMediaUrl(project.thumbnail)}
                    alt={project.project_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5ead8, #fdf4e0)' }}>
                    <Building2 className="w-14 h-14" style={{ color: 'rgba(201,168,76,0.3)' }} />
                  </div>
                )}
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="badge-featured text-[10px] px-2 py-0.5">
                    {project.project_status}
                  </span>
                  {project.is_hot_deal && (
                    <span className="badge-hot text-[10px] px-2 py-0.5 flex items-center gap-1">
                      <Flame className="w-3 h-3" /> Hot
                    </span>
                  )}
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.9)', color: '#2a1e00', fontFamily: "'Josefin Sans', sans-serif" }}>
                    {project.project_type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#c9a84c', fontFamily: "'Josefin Sans', sans-serif" }}>
                  {project.developer_name}
                </p>
                <h3 className="text-base font-bold mb-3 group-hover:text-[#8a6a10] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#2a1e00' }}>
                  {project.project_name}
                </h3>

                <div className="space-y-1.5 mb-3">
                  {project.sector && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: '#6a5a30' }}>
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                      <span>{project.sector}{project.micro_location ? `, ${project.micro_location}` : ''}</span>
                    </div>
                  )}
                  {(project.size_min || project.size_max) && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: '#6a5a30' }}>
                      <Ruler className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                      <span>{project.size_min || '—'} - {project.size_max || '—'} sq.ft.</span>
                    </div>
                  )}
                  {project.starting_price ? (
                    <div className="flex items-center gap-2 text-xs">
                      <IndianRupee className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                      <span className="font-semibold" style={{ color: '#2a1e00' }}>Starting {project.starting_price}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs">
                      <IndianRupee className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
                      <span className="font-semibold italic" style={{ color: '#8a7a50' }}>Price on Request</span>
                    </div>
                  )}
                </div>

                {project.unit_types?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.unit_types.slice(0, 3).map((type) => (
                      <span key={type} className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: '#fdf4e0', color: '#8a6a10' }}>
                        {type}
                      </span>
                    ))}
                    {project.unit_types.length > 3 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: '#fdf8ee', color: '#8a7a50' }}>
                        +{project.unit_types.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
                  <Link to={`/project/${project.id}`} className="flex-1">
                    <Button className="w-full btn-primary text-[10px] py-2 px-3" data-testid={`view-project-${project.id}`}>
                      View Details
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="flex-1 btn-secondary text-[10px] py-2 px-3"
                    onClick={(e) => { e.preventDefault(); onEnquire?.(); }}
                  >
                    Enquire
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      {filteredProjects.length > visibleCount && (
        <div className="flex justify-center gap-1.5 mt-8">
          {Array.from({ length: maxIndex + 1 }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{
                background: i === currentIndex ? '#c9a84c' : 'rgba(201,168,76,0.2)',
                transform: i === currentIndex ? 'scale(1.3)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectSlider;
